import request from '@/utils/ajax';

/**
 * 登录用户信息
 *
 * @export
 * @interface LoginUser
 */
export interface LoginUser {
  uuid: string;
  nickname: string; // 昵称
  avatar: string; // 头像
  mobile: string; // 脱敏手机号
  id: string; //
  uid: string; // 用户id
  openid: string; // 智慧科协openid
  token: string; // 登录token
  expires_in: number; // 过期时间戳（秒）
  message: string; // 登录提示信息，例如：每日登录 +20
}

/**
 * 用户信息
 *
 * @export
 * @interface UserInfo
 */
export interface UserInfo {
  /**
   * 科普志愿者类型  1非个人
   */
  apply_level: number;

  /**
   * 科普员申请审核状态
   * 0 - 未申请
   * 1 - 待审核
   * 2 - 被拒绝
   * 3 - 已通过
   */
  apply_status: string;

  /**
   * 头像
   */
  avatar: string;

  /**
   * 生日
   */
  birthday: string;

  /**
   * 收藏数
   */
  collect_count: string;

  /**
   * 评论数
   */
  comment_count: number;

  /**
   * 关注数
   */
  concern: number;

  /**
   * 受教育程度
   */
  edu: string;

  /**
   * 经验值
   */
  experience: number;

  /**
   * 经验值图标
   */
  experience_icon: string;

  /**
   * 来自经验
   */
  from_experience: string;

  /**
   * 来自等级
   */
  from_grade: string;

  /**
   * 完整级别
   */
  full_level: number;

  /**
   * 当前级别名
   */
  grade_name: string;

  /**
   * 附加信息
   */
  information: string;

  /**
   * 是否为科学家
   */
  is_sciencer: number;

  /**
   * 是否有奖杯
   */
  is_trophy: number;

  /**
   * 0-未升级成科普号、1-已升级成科普号 2禁用
   */
  is_kepuhao: number;

  /**
   * 科普员是否待审核状态 1是审核中状态，2是非审核状态
   */
  kp_apply_status: number;

  /**
   * 科普号状态
   * -1未申请 0待审核 1通过 3未通过
   */
  kph_status: number;

  /**
   * 科普号状态文案
   */
  kph_status_msg: string;

  /**
   * 经验标签
   */
  label_experience: number;

  /**
   * 职位标签
   */
  label_position: number;

  /**
   * 月经验值
   */
  month_score: number;

  /**
   * 昵称
   */
  nickname: string;

  /**
   * 简介
   */
  profile: string;

  /**
   * 排行
   */
  rank: string;

  /**
   * 性别
   */
  sex: string;

  /**
   * 预约数
   */
  subscribe_count: string;

  /**
   * 下一个经验值阈值
   */
  to_experience: string;

  /**
   * 下一个等级名
   */
  to_grade: string;

  /**
   * 总经验值
   */
  total_score: number;

  // 用户uid
  uid: string;

  /**
   * UUID
   */
  uuid: string;
}

/**
 * 科普员认证信息
 * @export
 * @interface ScienceInfo
 */
export interface ScienceInfo {
  /** 市级编号 */
  city_code: string;
  /** 单位ID */
  company_id: string;
  /**  单位名称（手动输入） */
  company_name: string;
  /** 县级编号 */
  county_code: string;
  /**  单位名称（下拉选中） */
  cp_name: string;
  /**  身份证 */
  identity_id: string;
  /** 是否科技工作者(1-是 2-否) */
  is_kj: '1' | '2';
  /**  '机构ID */
  it_id: string;
  it_name: string;
  /**  职称id */
  job_title_id: string;
  job_title_name: string;
  /** 科普员名 */
  name: string;
  /**科普员协议状态 */
  protocol: '' | '1' | '2';
  /**省级编号 */
  province_code: string;
  /**科普员类型 */
  science_type: string;
  science_type_name: string;
  /**学科分类id */
  subject_id: string;
  subject_name: string;
  /**街道、乡镇编号 */
  town_code: string;
  /**人机校验参数 */
  verify: string;
  pickerTitle: string;
}

/**
 * 账号密码登录
 *
 * @param {object} data - 包含登录数据的对象。
 *   @property {string} country_code - 用户手机号的国家代码。
 *   @property {string} mobile - 用户手机号。
 *   @property {string} password - 用户密码。
 *   @property {number} source - 登录请求的来源（1、2 或 3），1网站，2ios，3android
 * @return {Promise<any>} 一个解析为服务器响应的 Promise。
 */
export function reqLoginForAccount(data: {
  country_code: string;
  mobile: string;
  password: string;
  source: 1 | 2 | 3;
  device_id: string;
  network: string;
}) {
  return request.request<LoginUser, HttpResponse<LoginUser>>({
    url: '/Login/login',
    method: 'post',
    data: data,
  });
}

/**
 * 手机号验证码登录
 *
 * @param {object} data - 包含登录数据的对象。
 *   @property {string} country_code - 用户手机号的国家代码。
 *   @property {string} mobile - 用户手机号。
 *   @property {string} verify - 短信验证码。
 *   @property {string} device_id - 设备唯一标识
 *   @property {string} network - 网络类型
 * @return {Promise<any>} 一个解析为服务器响应的 Promise。
 */
export function reqLoginForVerify(data: {
  country_code: string;
  mobile: string;
  verify: string;
  device_id: string;
  network: string;
}) {
  return request.request<LoginUser, HttpResponse<LoginUser>>({
    url: '/Login/loginByVerify',
    method: 'post',
    data: data,
  });
}

/**
 * 忘记密码，重置密码
 *
 * @param {object} data - 包含登录数据的对象。
 *   @property {string} country_code - 用户手机号的国家代码。
 *   @property {string} mobile - 用户手机号。
 *   @property {string} verify - 短信验证码。
 *   @property {string} password - 用户密码。
 *   @property {string} device_id - 设备唯一标识
 *   @property {string} network - 网络类型
 * @return {Promise<any>} 一个解析为服务器响应的 Promise。
 */
export function reqResetPassword(data: {
  country_code: string;
  mobile: string;
  verify: string;
  password: string;
}) {
  return request.request<void, HttpResponse<void>>({
    url: '/Login/resetpass',
    method: 'post',
    data: data,
  });
}

/**
 * 获取验证码
 *
 * @param {object} data - 包含以下属性的对象：
 *   - country_code (string): 手机号码的国家代码。
 *   - mobile (string): 要发送短信的手机号码。
 *   - type ('1' | '2' | '3' | '4' | '5' | '6'): 要发送的短信类型， 1、注册 2、修改 3、找回 4、登录 5、修改手机号 , 6绑定手机号
 * @return {Promise} 一个 Promise，解析为服务器的响应。
 */
export function reqSendVerifySms(data: {
  country_code: string;
  mobile: string;
  type: '1' | '2' | '3' | '4' | '5' | '6';
}) {
  return request.request<void, HttpResponse<void>>({
    url: '/Login/sendVerifySms',
    method: 'post',
    data: data,
  });
}
/**
 * 获取验证码(仅登录时使用)
 *
 * @param {object} data - 包含以下属性的对象：
 *   - country_code (string): 手机号码的国家代码。
 *   - mobile (string): 要发送短信的手机号码。
 *   - type ('1' | '2' | '3' | '4' | '5' | '6'): 要发送的短信类型， 1、注册 2、修改 3、找回 4、登录 5、修改手机号 , 6绑定手机号
 * @return {Promise} 一个 Promise，解析为服务器的响应。
 */
export function reqSendVerifySmsNew(data: {
  country_code: string;
  mobile: string;
  type: '1' | '2' | '3' | '4' | '5' | '6';
}) {
  return request.request<void, HttpResponse<void>>({
    url: '/Login/sendVerifySmsNew',
    method: 'post',
    data: data,
  });
}

/**
 * 用户注册
 *
 * @param {object} data - 包含登录数据的对象。
 *   @property {string} country_code - 用户手机号的国家代码。
 *   @property {string} mobile - 用户手机号。
 *   @property {string} verify - 短信验证码。
 *   @property {string} password - 用户密码。
 * @return {Promise<any>} 一个解析为服务器响应的 Promise。
 */
export function reqRegister(data: {
  country_code: string;
  mobile: string;
  verify: string;
  password: string;
}) {
  return request.request<void, HttpResponse<void>>({
    url: '/login/register',
    method: 'post',
    data,
    addToken: false,
  });
}

// 科普员个人信息
export function reqSciencerinfo() {
  return request.request({
    url: '/sciencer/sciencerinfo',
    method: 'post',
  });
}

// 科普员登录-用户信息
export function reqUserInfo() {
  return request.request<UserInfo, HttpResponse<UserInfo>>({
    url: '/user/userInfo',
    method: 'post',
  });
}
// 科普员认证-提交 /sciencer/scienceRegister
export function reqScienceRegister(data: ScienceInfo) {
  return request.request({
    url: '/sciencer/scienceRegister',
    method: 'post',
    data: data,
  });
}

// 科普员认证-更新信息 /sciencer/scienceRegister
export function reqScienceUpdate(data: ScienceInfo) {
  return request.request({
    url: '/sciencer/update',
    method: 'post',
    data: data,
  });
}

// 获取科普员类型 /sciencer/type
export function reqSciencerType() {
  return request.request({
    url: '/sciencer/type',
    method: 'get',
  });
}
// 科普员科技工作者职位信息 /Sciencer/sciencerrTitleList
export function reqSciencerrTitleList() {
  return request.request({
    url: '/Sciencer/sciencerrTitleList',
    method: 'get',
  });
}
// 获取机构/institution/institutionlist
export function reqInstitutionlist(params: { name: string }) {
  return request.request({
    url: '/institution/institutionlist',
    method: 'get',
    params,
  });
}
// 学科/Sciencer/sciencerSubjectList
export function reqSciencerSubjectList(params: { name: string }) {
  return request.request({
    url: '/Sciencer/sciencerSubjectList',
    method: 'get',
    params,
  });
}

// 科普员经验值,传播量
export function reqPersonalStatistics(data: {
  type: 1 | 2 | 3; // 1经验 2传播 3日活量
  uid: string; // 用户uid
  search_type: 1 | 2; // 1 近一月 2近一年
}) {
  return request.request({
    url: '/scientist/personalStatistics',
    method: 'post',
    data,
  });
}

/**
 * 通过科协令牌请求登录
 * @param {Object} data - 包含科协令牌的对象
 * @param {string} data.zhkx_token - 科协令牌
 * @returns {Promise} 返回请求的Promise对象
 */
export function reqLoginByKeXieToken(data: { zhkx_token: string }) {
  return request.request({
    url: '/login/getUserByLcToken',
    method: 'get',
    params: data,
    addToken: false,
  });
}
